import { Switch } from 'antd'
import { ListResponseSspListItemBean, SspBean, SspListItemBean } from 'common/api/api.ts'
import { sspApi } from 'common/api/entitiesApi.tsx'
import { ActiveStatusSelector } from 'common/components/dictionary/ActiveStatusSelector.tsx'
import { SspTypeSelector } from 'common/components/dictionary/SspTypeSelector.tsx'
import { ListCard } from 'common/components/ListCard.tsx'
import { AgencySelector } from 'common/components/reloadable/AgencySelector.tsx'
import { SearchInput } from 'common/components/SearchInput.tsx'
import { ListFilters, useAppContext } from 'common/context/AppContext.tsx'
import { CommonRoutes } from 'common/context/commonRoutes.ts'
import { SspActionBar } from 'common/pages/ssp/SspActionBar.tsx'
import { COLUMN_ID_WIDTH, COLUMN_NAME_WIDTH } from 'common/util/constants.ts'
import { initListFilters, useListFilters } from 'common/util/useListFilters.ts'
import { apiCall } from 'common/util/Util.tsx'
import dayjs from 'dayjs'
import { useState } from 'react'
import { Link } from 'react-router-dom'


interface SspListProps {
    admin: boolean
}

const SspList = ({ admin }: SspListProps) => {
    const [listData, setListData] = useState<ListResponseSspListItemBean>()
    const [filters, setFilters] = useState<ListFilters>(initListFilters({sort: {name: 'id', direction: 'desc'}}))
    const {setFilter} = useListFilters(setFilters)

    const appContext = useAppContext()
    const [loading, setLoading] = useState<boolean>(false)

    const toggleActive = (id: number, checked: boolean) => {
        apiCall(setLoading, appContext,
            () => sspApi.changeSspStatus(id),
            () => setListData((prev) =>
                prev
                    ? {
                        ...prev,
                        data: prev.data?.map((item) =>
                            item.data?.id === id ? {...item, data: {...item.data, active: checked}} : item
                        )
                    }
                    : prev
            ))
    }

    const loadData = () => {
        apiCall(setLoading, appContext,
            () => sspApi.sspPage(
                filters.sort?.name,
                filters.sort?.direction,
                filters.name,
                filters.active,
                filters.sspType,
                filters.agencyId,
                { query: { page: filters.page, size: filters.pageSize } }
            ),
            (bean: ListResponseSspListItemBean) => setListData(bean)
        )
    }

    return (
        <ListCard
            loading={loading}
            title="SSPs"
            listRouteKey={CommonRoutes.SSP}
            filters={filters}
            setFilters={setFilters}
            listData={listData}
            loadData={loadData}
            createName="SSP"
            createRoute={CommonRoutes.SSP_CREATE}
            columns={[
                {
                    title: 'ID',
                    dataIndex: 'id',
                    width: COLUMN_ID_WIDTH,
                    fixed: 'left'
                },
                {
                    title: 'Name',
                    dataIndex: 'name',
                    sorter: true,
                    render: (text, record: SspBean) => (
                        <Link
                            style={{ color: '#5888CF' }}
                            to={`${CommonRoutes.SSP}/${record.id}/edit`}>
                            {text}
                        </Link>),
                    className: 'list-table-name',
                    ellipsis: true,
                    width: COLUMN_NAME_WIDTH,
                    fixed: 'left'
                },
                {
                    title: 'Internal Name',
                    dataIndex: 'internalName',
                    sorter: true,
                    className: 'list-table-name',
                    ellipsis: true,
                    width: COLUMN_NAME_WIDTH,
                },
                {
                    title: 'Endpoint',
                    dataIndex: 'endpoint',
                    sorter: true,
                    showSorterTooltip: false,
                    className: 'list-table-name',
                    ellipsis: true,
                    width: COLUMN_NAME_WIDTH,
                },
                {
                    title: 'Type',
                    dataIndex: 'sspType',
                    sorter: true,
                    width: 120,
                },
                {
                  title: 'Agency',
                  dataIndex: 'agencyName',
                  hidden: !admin,
                  width: 120,
                  render: (_: any, record: SspListItemBean) =>
                    record.agencyId !== undefined ? (
                      <Link style={{ color: '#5888CF' }} to={`/agency/${record.agencyId}/edit`}>
                        {record.agencyName}
                      </Link>
                    ) : (
                      '-'
                    ),
                },
                {
                    title: "Last Modified",
                    dataIndex: "modified",
                    sorter: true,
                    width: 100,
                    render: (date) => date && dayjs(date).format('DD MMM YYYY')
                },
                {
                    title: 'Active',
                    dataIndex: 'active',
                    sorter: true,
                    render: (_text: string, record: SspBean) => (
                        <Switch
                            size="small"
                            checked={record.active}
                            onChange={(checked) => toggleActive(record.id as number, checked)}
                        />
                    ),
                    width: 60,
                },
                {
                    title: 'Actions',
                    render: (_text: string, record: SspBean) => (
                        <SspActionBar
                            record={record}
                        />
                    ),
                    width: 240
                }
            ]}
        >
            <SearchInput
                value={filters.name}
                onChange={(value) => setFilter('name', value)}
                placeholder="Search by ID, Name, Endpoint"
                width="250px"
            />
            {admin && <AgencySelector
              placeholder="Filter by Agency"
              style={{ minWidth: '200px' }}
              value={filters.agencyId}
              onChange={(value: number) => setFilter('agencyId', value)}
            />}
            <SspTypeSelector
                placeholder="SSP Type"
                style={{ minWidth: '140px' }}
                value={filters.sspType}
                onChange={(value) => setFilter('sspType', value)}
            />
            <ActiveStatusSelector
                placeholder="Filter by Active"
                style={{ minWidth: '140px' }}
                value={filters.active}
                onChange={(value) => setFilter('active', value)}
            />
        </ListCard>
    )
}

export default SspList
